import { Component, computed, OnInit, Signal, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { AsyncPipe, NgClass } from '@angular/common';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../../store/app/app.selectors';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { ProfileComponent } from './profile/profile.component';
import { updateParamsObject } from '../../../shared/utils/filter-utils';
import { HttpParams } from '@angular/common/http';
import { BankingDetailsComponent } from './banking-details/banking-details.component';
import { BankingStatus } from '../../../_interfaces/business.interface';
import { InfoCardHeaderComponent } from '../../../shared/components/info-card-header/info-card-header.component';
import { BusinessDetailsComponent } from './business-details/business-details.component';
import { UpdatePageTitle } from '../../../store/app/app.actions';
import { OffcanvasService } from '../../../_services/offcanvas-service/offcanvas.service';
import { BusinessService } from '../../../_services/business-service/business.service';

@Component({
  selector: 'app-rules',
  imports: [
    RouterOutlet,
    AsyncPipe,
    AvatarComponent,
    NgbCollapse,
    ProfileComponent,
    BankingDetailsComponent,
    InfoCardHeaderComponent,
    BusinessDetailsComponent,
    NgClass,
  ],
  templateUrl: './settings.component.html',
})
export class SettingsComponent implements OnInit {
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly store = inject(Store);
  readonly businessService = inject(BusinessService);
  readonly offcanvasService = inject(OffcanvasService);

  readonly userProfile$ = this.store.select(AppSelectors.userDetails);
  @ViewChild('profileCollapse') profileCollapse!: NgbCollapse;
  @ViewChild('businessCollapse') businessCollapse!: NgbCollapse;
  @ViewChild('bankingCollapse') bankingCollapse!: NgbCollapse;

  readonly pageParam: Signal<string> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return queryParams['page'] as string;
  });

  readonly isProfile: Signal<boolean> = computed(() => this.pageParam() === 'profile');
  readonly isBusiness: Signal<boolean> = computed(() => this.pageParam() === 'business');
  readonly isBanking: Signal<boolean> = computed(() => this.pageParam() === 'banking');

  private queryParams = new HttpParams();
  protected isProfileCollapsed = !this.isProfile();
  protected isBusinessCollapsed = !this.isBusiness();
  protected isBankingCollapsed = !this.isBanking();

  protected bankingLoaded = false;
  bankStatus?: BankingStatus;
  protected readonly bankingStatus = BankingStatus;

  protected bankingDetails = computed(() => this.businessService.bankingDetails());

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Settings'));
    this.offcanvasService.closeAfterNavigationEnd();
  }

  ngOnInit(): void {
    // stop unnecessary API calls
    if (this.isBanking() && !this.bankingDetails()?.bank) {
      this.businessService.fetchBankingDetails();
      this.bankingLoaded = true;
    }
  }

  onOpenProfile() {
    this.closeAllBut('profile');
    this.updateUrl('profile');
    this.profileCollapse.toggle();
  }

  onOpenBusiness() {
    this.closeAllBut('business');
    this.updateUrl('business');
    this.businessCollapse.toggle();
  }

  onOpenBanking() {
    if (!this.bankingLoaded) {
      this.businessService.fetchBankingDetails();
    }
    this.closeAllBut('banking');
    this.bankingLoaded = true;
    this.updateUrl('banking');
    this.bankingCollapse.toggle();
  }

  closeAllBut(arg: string) {
    this.isProfileCollapsed = arg !== 'profile';
    this.isBusinessCollapsed = arg !== 'business';
    this.isBankingCollapsed = arg !== 'banking';
  }

  updateUrl(url: string) {
    this.queryParams = this.queryParams.set('page', url);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: updateParamsObject(this.queryParams),
    });
  }
}
